import axios, { AxiosInstance, AxiosResponse } from 'axios';

export interface Pedido {
  id: string;
  cliente: string;
  produtos: Array<{
    nome: string;
    quantidade: number;
    valor: number;
  }>;
  valorTotal: number;
  status: 'RECEBIDO' | 'EM_PREPARO' | 'PRONTO' | 'EM_ROTA' | 'ENTREGUE' | 'CANCELADO';
  dataCriacao: string;
  dataEntrega?: string;
  enderecoEntrega: {
    rua: string;
    numero: string;
    bairro: string;
    cidade: string;
    estado: string;
    cep: string;
  };
}

export interface CreatePedidoRequest {
  cliente: string;
  produtos: Array<{
    nome: string;
    quantidade: number;
    valor: number;
  }>;
  enderecoEntrega: {
    rua: string;
    numero: string;
    bairro: string;
    cidade: string;
    estado: string;
    cep: string;
  };
}

class PedidoService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3000/api',
      timeout: 10000,
    });

    // Interceptor para adicionar token JWT
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
  }

  async getPedidos(): Promise<Pedido[]> {
    try {
      const response: AxiosResponse<Pedido[]> = await this.client.get('/pedidos');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao buscar pedidos');
    }
  }

  async getPedidoById(pedidoId: string): Promise<Pedido> {
    try {
      const response: AxiosResponse<Pedido> = await this.client.get(`/pedidos/${pedidoId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao buscar pedido');
    }
  }

  async createPedido(pedidoData: CreatePedidoRequest): Promise<Pedido> {
    try {
      const response: AxiosResponse<Pedido> = await this.client.post('/pedidos', pedidoData);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao criar pedido');
    }
  }

  async updatePedidoStatus(pedidoId: string, status: Pedido['status']): Promise<Pedido> {
    try {
      const response: AxiosResponse<Pedido> = await this.client.patch(`/pedidos/${pedidoId}/status`, {
        status,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao atualizar status do pedido');
    }
  }

  async deletePedido(pedidoId: string): Promise<void> {
    try {
      await this.client.delete(`/pedidos/${pedidoId}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao deletar pedido');
    }
  }
}

export const pedidoService = new PedidoService();
export default pedidoService;
